export interface LocationOption {
    id: string;
    type: 'actual' | 'home' | 'gps' | 'custom' | 'none';
    displayName: string;
    address: string | null;
    addressData?: RequestLocationAddress | null;
    selected: boolean;
    latitude?: number | null;
    longitude?: number | null;
    display: boolean;
    entireStreet?: boolean;
}

export interface StatusType {
    name: string;
    _id: string;
    color: string;
    isDefault: boolean;
    isDeletable: boolean;
    type?: string;
}

export interface DepartmentType {
    name: string;
    _id: string;
    color: string;
}

export interface DateRange {
    start: Date;
    end: Date;
}

export interface RequestLocation {
    geometry?: GeoJSONGeometry;
    address?: RequestLocationAddress;
    entireStreet?: boolean;
    deleted?: boolean;
}

export interface RequestSubtask {
    _id: string;
    description: string;
    isDone: boolean;
    completedAt?: string;
    dueDate?: string | null;
    assignedTo?: { _id: string; firstName: string; lastName: string; email: string; picture: string };
}

export interface RequestHistory {
    date: Date;
    actionKey: string;
    type: string;
    user: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        picture: string;
    };
    metadata?: any;
    _id: string;
    deleted: boolean;
    createdAt: Date;
    updatedAt: Date;
    message: string;
}

export interface RequestMessage {
    _id: string;
    id?: string;
    sender: {
        _id: string;
        firstName: string;
        lastName: string;
        picture: string;
    };
    message: string;
    sentAt: string | Date;
    attachments?: string[];
    deleted?: boolean;
    isAdmin?: boolean; // True if message is from an admin
}

export enum RequestRecurrence {
    Daily = 'DAILY',
    Weekly = 'WEEKLY',
    Monthly = 'MONTHLY',
    Yearly = 'YEARLY',
}

export interface RequestCategoryType {
    _id: string;
    name: string;
    description: string;
    icon: string;
    color: string;
    order: number;
    subCategories: RequestSubCategoryType[];
    department: DepartmentType;
    assignedTo: string[];
    slaInDays: number | null;
    statuses: StatusType[];
    forcePrivateRequests?: boolean;
}

export interface RequestSubCategoryType {
    _id: string;
    name: string;
    slaInDays?: number | null;
}

export interface RequestFile {
    url: string;
    originalName: string;
}

export interface Request {
    _id: string;
    requestId: string;
    icon: string;
    description: string;
    category: RequestCategoryType;
    department: DepartmentType;
    subCategory?: RequestSubCategoryType;
    user: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        picture: string;
    };
    organisation: string;
    state: string;
    isPrivate: boolean | null;
    sla: string | null;
    assignedTo: string;
    location: RequestLocation;
    files: RequestFile[];
    internalFiles?: RequestFile[];
    subtasks: RequestSubtask[];
    history: RequestHistory[];
    messages: RequestMessage[];
    adminUnreadMessageCount: number;
    recurrence: RequestRecurrence | null;
    createdAt?: Date;
    internalNotes: InternalNote[];
    internalDescription?: string | null;
    links: RequestLink[];
    tags?: string[];
}

export enum RequestLinkType {
    BLOCKS = 'BLOCKS',
    IS_BLOCKED_BY = 'IS_BLOCKED_BY',
    IS_DUPPLICATE_OF = 'IS_DUPPLICATE_OF', // NOT a spelling error
    IS_LINKED_TO = 'IS_LINKED_TO',
}

export interface RequestLink {
    _id?: string;
    type: RequestLinkType;
    linkedRequest: Request; // Or a subset, but user JSON showed full object structure for linkedRequest roughly
    linkedRequestId?: string; // For API payload convenience
}

export interface InternalNote {
    _id?: string;
    date: Date;
    user: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        picture: string;
    };
    description: string;
    edited?: boolean;
}

export interface RequestViewModel {
    _id: string;
    category: {
        _id: string;
        name: string;
        color: string;
        icon: string;
    };
    requestId: string;
    location?: string;
    coordinates?: [number, number];
    creationDate: string;
    subCategory?: RequestSubCategoryType;
    status: {
        name: string;
        _id: string;
        color: string;
    };
    categoryStatuses?: StatusType[];
    department: {
        name: string;
        _id: string;
        color: string;
    };
    reporter: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        picture: string;
    };
    reporterType: string;
    createdAt: string;
    expectedEnd: string;
    isPrivate: boolean;
    adminUnreadMessageCount?: number;
    tags?: string[];
}

export enum GeoJSONType {
    Point = 'Point',
    LineString = 'LineString',
    Polygon = 'Polygon',
    MultiPoint = 'MultiPoint',
    MultiLineString = 'MultiLineString',
    MultiPolygon = 'MultiPolygon',
    GeometryCollection = 'GeometryCollection',
}

export interface GeoJSONGeometry {
    type: GeoJSONType | string;
    coordinates: number[];
}

export interface RequestLocationAddress {
    formattedAddress: string;
    street?: string;
    streetName?: string;
    number?: string;
    postCode?: string;
    city?: string;
    province?: string;
    country?: string;
    typeStreet?: string;
    direction?: string;
    unit?: string;
}
